import * as THREE from "three";
import { EnergyLine } from "./EnergyLine.js";

export class Grid {
  constructor(scene) {
    this.scene = scene;
    this.gridSize = 7;
    this.cellSize = 1;
    // Adjust spacing based on screen size
    const isMobile = window.innerWidth <= 768;
    this.spacing = isMobile ? 0.9 : 1.2; // Even tighter spacing on mobile
    this.crystals = [];
    this.soundManager = null;
    this.activeEnergyLines = [];
    this.chakraColors = {
      // The 7 chakras in order from root to crown
      red: 0xe31e24, // Muladhara (Root Chakra)
      orange: 0xffa500, // Svadhishthana (Sacral Chakra)
      yellow: 0xffed00, // Manipura (Solar Plexus Chakra)
      green: 0x00ab4e, // Anahata (Heart Chakra)
      blue: 0x00b9e4, // Vishuddha (Throat Chakra)
      indigo: 0x0033cc, // Ajna (Third Eye Chakra)
      purple: 0x9b26b6, // Sahasrara (Crown Chakra)
    };
    this.crystalRates = {
      positiveRate: 0.6,
      negativeRate: 0.3,
      neutralRate: 0.1,
    };
    this.activeChakras = ["red"]; // Default to root chakra

    // Update spacing on window resize
    window.addEventListener("resize", () => {
      const isMobileNow = window.innerWidth <= 768;
      this.spacing = isMobileNow ? 0.9 : 1.2;
      // Recreate grid with new spacing if it exists
      if (this.gridMesh) {
        this.createGrid();
      }
    });
  }

  setSoundManager(soundManager) {
    this.soundManager = soundManager;
  }

  setLevelConfig(config) {
    if (config.gridConfig) {
      this.crystalRates = {
        positiveRate: config.gridConfig.positiveRate,
        negativeRate: config.gridConfig.negativeRate,
        neutralRate: config.gridConfig.neutralRate,
      };
      this.activeChakras = config.gridConfig.activeChakras || ["red"];
    }
  }

  clearGrid() {
    // Remove all existing crystals
    for (let row = 0; row < this.gridSize; row++) {
      for (let col = 0; col < this.gridSize; col++) {
        if (this.crystals[row] && this.crystals[row][col]) {
          const crystal = this.crystals[row][col];
          if (crystal && crystal.mesh) {
            this.scene.remove(crystal.mesh);
            crystal.mesh.clear();
            crystal.crystalMesh.geometry.dispose();
            crystal.crystalMesh.material.dispose();
            crystal.crystalMesh = null;
            crystal.mesh = null;
          }
        }
      }
    }
    // Reset crystals array
    this.crystals = [];

    // Remove existing grid mesh if it exists
    if (this.gridMesh) {
      this.scene.remove(this.gridMesh);
      this.gridMesh.geometry.dispose();
      this.gridMesh.material.dispose();
      this.gridMesh = null;
    }
  }

  createGrid() {
    // Clear existing grid first
    this.clearGrid();

    // Create grid base
    const gridGeometry = new THREE.PlaneGeometry(
      this.gridSize * this.spacing,
      this.gridSize * this.spacing
    );
    const gridMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a4a6a,
      transparent: true,
      opacity: 0.25,
      metalness: 0.2,
      roughness: 0.8,
    });
    this.gridMesh = new THREE.Mesh(gridGeometry, gridMaterial);
    this.gridMesh.rotation.x = -Math.PI / 2;
    this.gridMesh.position.y = 0.1;
    this.scene.add(this.gridMesh);

    // Calculate grid offset to center crystals
    const offset = ((this.gridSize - 1) * this.spacing) / 2;

    // Initialize crystal grid
    for (let row = 0; row < this.gridSize; row++) {
      this.crystals[row] = [];
      for (let col = 0; col < this.gridSize; col++) {
        this.createCrystal(row, col, offset);
      }
    }

    // Ensure we have enough crystals of each required color
    if (!this.checkRequiredCrystals()) {
      this.forceSpawnRequiredCrystals();
    }
  }

  createCrystal(row, col, offset) {
    // Remove any existing crystal at this position
    if (this.crystals[row] && this.crystals[row][col]) {
      const existingCrystal = this.crystals[row][col];
      if (existingCrystal.mesh) {
        this.scene.remove(existingCrystal.mesh);
      }
      this.crystals[row][col] = null;
    }

    // Create crystal container
    const container = new THREE.Object3D();
    container.isCrystal = true;

    // Create crystal geometry
    const geometry = new THREE.OctahedronGeometry(this.cellSize * 0.3);

    // Get crystal type and color based on active chakras
    const type = this.getRandomCrystalType();
    const color = type === "positive" ? this.getRandomChakraColor() : null;
    const material = this.getCrystalMaterial(type, color);

    const crystalMesh = new THREE.Mesh(geometry, material);
    // Mark both container and mesh for raycasting
    crystalMesh.isCrystal = true;
    crystalMesh.crystalContainer = container;
    container.crystalMesh = crystalMesh;
    container.add(crystalMesh);

    // Add larger invisible hit area for better touch targets
    const hitAreaGeometry = new THREE.PlaneGeometry(
      this.spacing * 0.95,
      this.spacing * 0.95
    );
    const hitAreaMaterial = new THREE.MeshBasicMaterial({
      visible: false,
      transparent: true,
      opacity: 0,
      depthTest: false,
      side: THREE.DoubleSide,
    });
    const hitArea = new THREE.Mesh(hitAreaGeometry, hitAreaMaterial);
    hitArea.isCrystal = true;
    hitArea.crystalContainer = container;

    // Position the hit area
    hitArea.rotation.x = -Math.PI / 2;
    hitArea.position.y = 0.01;

    container.add(hitArea);

    // Position crystal in grid
    const x = col * this.spacing - offset;
    const z = row * this.spacing - offset;
    container.position.set(x, 0.5, z);

    this.scene.add(container);
    this.crystals[row][col] = {
      mesh: container,
      crystalMesh: crystalMesh,
      type: type,
      color: color,
      isLocked: false,
      isEnergized: false,
    };

    // Return the created crystal for reference
    return this.crystals[row][col];
  }

  removeCrystal(row, col) {
    const crystal = this.crystals[row][col];
    if (crystal && crystal.mesh) {
      // Remove from scene
      this.scene.remove(crystal.mesh);

      // Create removal effect
      this.createRemovalEffect(
        crystal.mesh.position.clone(),
        crystal.color || 0xffffff
      );

      // Clear all references
      crystal.mesh.clear();
      crystal.crystalMesh.geometry.dispose();
      crystal.crystalMesh.material.dispose();
      crystal.crystalMesh = null;
      crystal.mesh = null;
      this.crystals[row][col] = null;

      // Ensure we maintain minimum required crystals
      if (!this.checkRequiredCrystals()) {
        this.forceSpawnRequiredCrystals();
      }
    }
  }

  createRemovalEffect(position, color) {
    const particleCount = 30;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const velocities = [];
    const scales = [];
    const rotations = [];

    // Create initial spiral formation
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 4;
      const radius = 0.05 * (i / particleCount);

      positions[i * 3] = position.x + Math.cos(angle) * radius;
      positions[i * 3 + 1] = position.y;
      positions[i * 3 + 2] = position.z + Math.sin(angle) * radius;

      // Slightly faster upward spiral motion
      velocities.push(
        new THREE.Vector3(
          Math.cos(angle) * 0.015, // Increased from 0.01
          0.03 + Math.random() * 0.01, // Increased from 0.02
          Math.sin(angle) * 0.015 // Increased from 0.01
        )
      );

      // Random rotation for each particle
      rotations.push({
        axis: new THREE.Vector3(
          Math.random() - 0.5,
          Math.random() - 0.5,
          Math.random() - 0.5
        ).normalize(),
        speed: Math.random() * 0.01 + 0.005,
      });

      scales.push(0.05 + Math.random() * 0.05);
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: color,
      size: 0.15,
      transparent: true,
      opacity: 1,
      map: this.createParticleTexture(),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const particles = new THREE.Points(geometry, material);
    this.scene.add(particles);

    let time = 0;
    const animate = () => {
      time += 0.024; // Increased from 0.016 for faster overall animation
      const positions = particles.geometry.attributes.position.array;

      for (let i = 0; i < particleCount; i++) {
        // Update position with velocity
        positions[i * 3] += velocities[i].x;
        positions[i * 3 + 1] += velocities[i].y;
        positions[i * 3 + 2] += velocities[i].z;

        // Add tighter spiral motion
        const spiralAngle = time * 3 + (i / particleCount) * Math.PI * 2; // Increased from time * 2
        const spiralRadius = 0.15 * (time * 0.7); // Increased from 0.5
        positions[i * 3] += Math.cos(spiralAngle) * 0.0005 * spiralRadius;
        positions[i * 3 + 2] += Math.sin(spiralAngle) * 0.0005 * spiralRadius;

        // Slightly faster slowdown
        velocities[i].y *= 0.97; // Increased from 0.98 for faster slowdown

        // Add gentler wind effect
        velocities[i].x += Math.sin(time + i) * 0.00005;
        velocities[i].z += Math.cos(time + i) * 0.00005;
      }

      particles.geometry.attributes.position.needsUpdate = true;

      // Faster fade out
      material.opacity = Math.max(0, 1 - time * 0.7); // Increased from 0.5
      material.size = Math.max(0.03, 0.15 * (1 - time * 0.4)); // Increased from 0.3

      if (material.opacity > 0) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(particles);
        geometry.dispose();
        material.dispose();
      }
    };

    animate();
  }

  createParticleTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const context = canvas.getContext("2d");

    // Create a soft, glowing particle texture
    const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
    gradient.addColorStop(0.3, "rgba(255, 255, 255, 0.8)");
    gradient.addColorStop(0.7, "rgba(255, 255, 255, 0.3)");
    gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

    context.fillStyle = gradient;
    context.fillRect(0, 0, 64, 64);

    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  getCrystalMaterial(type, color) {
    switch (type) {
      case "negative":
        return new THREE.MeshStandardMaterial({
          color: 0x000000,
          metalness: 0.3,
          roughness: 0.7,
          emissive: 0x000000,
        });
      case "neutral":
        return new THREE.MeshStandardMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.9,
          metalness: 0.3,
          roughness: 0.3,
          emissive: 0x444444,
          emissiveIntensity: 0.4,
        });
      default:
        // For colored crystals (positive and color types)
        return new THREE.MeshStandardMaterial({
          color: color,
          metalness: 0.2,
          roughness: 0.3,
          emissive: color,
          emissiveIntensity: 0.7,
        });
    }
  }

  createGlowEffect(type, color) {
    const glowGeometry = new THREE.SphereGeometry(0.45, 32, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: type === "neutral" ? 0xffffff : color,
      transparent: true,
      opacity: 0.15,
      side: THREE.BackSide,
    });
    return new THREE.Mesh(glowGeometry, glowMaterial);
  }

  getRandomCrystalType() {
    const rand = Math.random();

    // Positive crystals (0 to positiveRate)
    if (rand < this.crystalRates.positiveRate) {
      return "positive";
    }

    // Negative crystals (positiveRate to positiveRate + negativeRate)
    if (
      rand <
      this.crystalRates.positiveRate + this.crystalRates.negativeRate
    ) {
      return "negative";
    }

    // Neutral crystals (remaining probability)
    return "neutral";
  }

  getRandomChakraColor() {
    // Only select from active chakras with higher probability
    if (!this.activeChakras || this.activeChakras.length === 0) {
      return this.chakraColors.red; // Fallback to red if no active chakras
    }

    const rand = Math.random();
    if (rand < 0.7) {
      // 70% chance to get active chakra color
      const color =
        this.activeChakras[
          Math.floor(Math.random() * this.activeChakras.length)
        ];
      return this.chakraColors[color];
    } else {
      // 30% chance to get any other color
      const allColors = Object.keys(this.chakraColors);
      const otherColors = allColors.filter(
        (color) => !this.activeChakras.includes(color)
      );
      if (otherColors.length === 0)
        return this.chakraColors[this.activeChakras[0]];
      const color = otherColors[Math.floor(Math.random() * otherColors.length)];
      return this.chakraColors[color];
    }
  }

  update() {
    // Update crystal animations
    const time = Date.now() * 0.001;
    this.crystals.forEach((row) => {
      row.forEach((crystal) => {
        if (crystal && crystal.mesh) {
          // Gentle floating animation
          crystal.mesh.position.y =
            0.5 + Math.sin(time + crystal.mesh.position.x) * 0.1;

          // Slow rotation
          crystal.crystalMesh.rotation.y = time * 0.5;
        }
      });
    });
  }

  getCrystalAtPosition(row, col) {
    // Check if position is within grid bounds
    if (row >= 0 && row < this.gridSize && col >= 0 && col < this.gridSize) {
      const crystal = this.crystals[row][col];
      if (crystal) {
        const offset = ((this.gridSize - 1) * this.spacing) / 2;
        const position = new THREE.Vector3(
          col * this.spacing - offset,
          0.5,
          row * this.spacing - offset
        );
        return { crystal, position };
      }
    }
    return null;
  }

  // Add new method to check if we have enough crystals of required colors
  checkRequiredCrystals() {
    const crystalCounts = {};
    this.activeChakras.forEach((chakra) => {
      crystalCounts[chakra] = 0;
    });

    // Count crystals of each active color
    for (let row = 0; row < this.gridSize; row++) {
      for (let col = 0; col < this.gridSize; col++) {
        const crystal = this.crystals[row][col];
        if (crystal && crystal.type === "positive") {
          // Convert hex color to chakra name
          const chakraName = Object.entries(this.chakraColors).find(
            ([_, value]) => value === crystal.color
          )?.[0];
          if (chakraName && this.activeChakras.includes(chakraName)) {
            crystalCounts[chakraName]++;
          }
        }
      }
    }

    // Check if we have at least 2 crystals of each required color
    return this.activeChakras.every((chakra) => crystalCounts[chakra] >= 2);
  }

  // Add method to force spawn required crystals
  forceSpawnRequiredCrystals() {
    const crystalCounts = {};
    this.activeChakras.forEach((chakra) => {
      crystalCounts[chakra] = 0;
    });

    // Count existing crystals
    for (let row = 0; row < this.gridSize; row++) {
      for (let col = 0; col < this.gridSize; col++) {
        const crystal = this.crystals[row][col];
        if (crystal && crystal.type === "positive") {
          const chakraName = Object.entries(this.chakraColors).find(
            ([_, value]) => value === crystal.color
          )?.[0];
          if (chakraName && this.activeChakras.includes(chakraName)) {
            crystalCounts[chakraName]++;
          }
        }
      }
    }

    // Force spawn missing crystals
    this.activeChakras.forEach((chakra) => {
      while (crystalCounts[chakra] < 2) {
        // Find empty spot or replaceable crystal
        let placed = false;
        for (let row = 0; row < this.gridSize && !placed; row++) {
          for (let col = 0; col < this.gridSize && !placed; col++) {
            const crystal = this.crystals[row][col];
            if (!crystal || crystal.type === "neutral") {
              const offset = ((this.gridSize - 1) * this.spacing) / 2;
              const newCrystal = this.createCrystal(row, col, offset);
              newCrystal.type = "positive";
              newCrystal.color = this.chakraColors[chakra];
              crystalCounts[chakra]++;
              placed = true;
            }
          }
        }
      }
    });
  }

  spawnNewCrystal(row, col) {
    // Calculate offset for positioning
    const offset = ((this.gridSize - 1) * this.spacing) / 2;

    // Create new crystal with animation
    const crystal = this.createCrystal(row, col, offset);

    // Add spawn animation
    if (crystal && crystal.mesh) {
      // Start with zero scale and hide initially
      crystal.mesh.scale.set(0, 0, 0);
      crystal.mesh.position.y = 0.1; // Start slightly above ground
      crystal.mesh.visible = false;

      // Add delay before starting animation
      setTimeout(() => {
        crystal.mesh.visible = true;

        // Animate scaling up
        const startTime = Date.now();
        const duration = 1000; // 1000ms animation (slowed down from 600ms)

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Elastic ease-out for bouncy effect
          const easeOutElastic = (t) => {
            const p = 0.4; // Increased period for slower bounce
            return (
              Math.pow(2, -10 * t) *
                Math.sin(((t - p / 4) * (2 * Math.PI)) / p) +
              1
            );
          };
          const currentProgress = easeOutElastic(progress);

          // Scale up
          const scale = currentProgress;
          crystal.mesh.scale.set(scale, scale, scale);

          // Rise up slightly
          crystal.mesh.position.y = 0.1 + (0.5 - 0.1) * currentProgress;

          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };

        animate();
      }, 300); // 300ms delay before starting animation
    }

    return crystal;
  }

  async setupTutorialLayout() {
    // Store current crystals for smooth transition
    const oldCrystals = [...this.crystals];

    // Clear the grid (but don't remove crystals yet)
    this.crystals = Array(this.gridSize)
      .fill(null)
      .map(() => Array(this.gridSize).fill(null));

    // Create tutorial layout
    const tutorialLayout = [
      { row: 3, col: 2, type: "positive", color: this.chakraColors.red },
      { row: 3, col: 4, type: "negative" },
      { row: 3, col: 6, type: "positive", color: this.chakraColors.red },
    ];

    // Calculate offset for positioning
    const offset = ((this.gridSize - 1) * this.spacing) / 2;

    // Create new crystals but make them invisible initially
    for (const crystal of tutorialLayout) {
      const newCrystal = this.createCrystal(crystal.row, crystal.col, offset);
      if (newCrystal) {
        newCrystal.type = crystal.type;
        newCrystal.color = crystal.color;

        // Update the crystal's material
        if (newCrystal.crystalMesh) {
          const material = this.getCrystalMaterial(crystal.type, crystal.color);
          material.transparent = true;
          material.opacity = 0;
          newCrystal.crystalMesh.material = material;
        }

        // Store references to tutorial crystals
        if (crystal.row === 3 && crystal.col === 2) {
          this.tutorialStartCrystal = newCrystal;
        } else if (crystal.row === 3 && crystal.col === 6) {
          this.tutorialEndCrystal = newCrystal;
        }
      }
    }

    // Fade out old crystals and fade in new ones
    const duration = 1000; // 1 second transition
    const startTime = Date.now();

    return new Promise((resolve) => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Fade out old crystals
        oldCrystals.forEach((row) => {
          row.forEach((crystal) => {
            if (
              crystal &&
              crystal.mesh &&
              crystal.crystalMesh &&
              crystal.crystalMesh.material
            ) {
              crystal.crystalMesh.material.transparent = true;
              crystal.crystalMesh.material.opacity = 1 - progress;
              crystal.mesh.scale.setScalar(1 - progress * 0.5);
            }
          });
        });

        // Fade in new crystals
        this.crystals.forEach((row) => {
          row.forEach((crystal) => {
            if (
              crystal &&
              crystal.mesh &&
              crystal.crystalMesh &&
              crystal.crystalMesh.material
            ) {
              crystal.crystalMesh.material.transparent = true;
              crystal.crystalMesh.material.opacity = progress;
              crystal.mesh.scale.setScalar(0.5 + progress * 0.5);
            }
          });
        });

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Remove old crystals
          oldCrystals.forEach((row) => {
            row.forEach((crystal) => {
              if (crystal && crystal.mesh) {
                this.scene.remove(crystal.mesh);
              }
            });
          });
          resolve();
        }
      };
      animate();
    });
  }

  async transitionToGameLayout(newLayout) {
    // Store current tutorial crystals
    const oldCrystals = [...this.crystals];

    // Clear the grid (but don't remove crystals yet)
    this.crystals = Array(this.gridSize)
      .fill(null)
      .map(() => Array(this.gridSize).fill(null));

    // Calculate offset for positioning
    const offset = ((this.gridSize - 1) * this.spacing) / 2;

    // Create new crystals but make them invisible initially
    for (const crystal of newLayout) {
      const newCrystal = this.createCrystal(crystal.row, crystal.col, offset);
      if (newCrystal) {
        newCrystal.type = crystal.type;
        newCrystal.color = crystal.color;

        // Update the crystal's material
        if (newCrystal.crystalMesh) {
          const material = this.getCrystalMaterial(crystal.type, crystal.color);
          material.transparent = true;
          material.opacity = 0;
          newCrystal.crystalMesh.material = material;
        }
      }
    }

    // Fade out old crystals and fade in new ones
    const duration = 1500; // Increase to 1.5 seconds for smoother transition
    const startTime = Date.now();

    return new Promise((resolve) => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Use easeInOutQuad for smoother transition
        const easedProgress =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        // Fade out old crystals
        oldCrystals.forEach((row) => {
          row.forEach((crystal) => {
            if (
              crystal &&
              crystal.mesh &&
              crystal.crystalMesh &&
              crystal.crystalMesh.material
            ) {
              crystal.crystalMesh.material.transparent = true;
              crystal.crystalMesh.material.opacity = 1 - easedProgress;
              crystal.mesh.scale.setScalar(1 - easedProgress * 0.5);
            }
          });
        });

        // Fade in new crystals
        this.crystals.forEach((row) => {
          row.forEach((crystal) => {
            if (
              crystal &&
              crystal.mesh &&
              crystal.crystalMesh &&
              crystal.crystalMesh.material
            ) {
              crystal.crystalMesh.material.transparent = true;
              crystal.crystalMesh.material.opacity = easedProgress;
              crystal.mesh.scale.setScalar(0.5 + easedProgress * 0.5);
            }
          });
        });

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Remove old crystals
          oldCrystals.forEach((row) => {
            row.forEach((crystal) => {
              if (crystal && crystal.mesh) {
                this.scene.remove(crystal.mesh);
              }
            });
          });

          // Make sure all new crystals are fully visible and properly scaled
          this.crystals.forEach((row) => {
            row.forEach((crystal) => {
              if (
                crystal &&
                crystal.mesh &&
                crystal.crystalMesh &&
                crystal.crystalMesh.material
              ) {
                crystal.crystalMesh.material.transparent = false;
                crystal.crystalMesh.material.opacity = 1;
                crystal.mesh.scale.setScalar(1);
              }
            });
          });

          resolve();
        }
      };
      animate();
    });
  }

  updateCrystalAppearance(crystal, type, color = null) {
    if (crystal && crystal.crystalMesh) {
      // Update material
      if (crystal.crystalMesh.material) {
        crystal.crystalMesh.material.dispose();
      }
      crystal.crystalMesh.material = this.getCrystalMaterial(type, color);
    }
  }

  createEnergyLine(startPos, endPos, color) {
    const energyLine = new EnergyLine({
      color: new THREE.Color(color),
      radius0: 0.1,
      radius1: 0.1,
      intensity: 1.5,
    });

    const points = [startPos, endPos];
    const mesh = energyLine.generatePath(points);
    if (mesh) {
      this.scene.add(mesh);
      this.activeEnergyLines.push(energyLine);
    }
    return energyLine;
  }

  async playTutorialConnection() {
    if (!this.tutorialStartCrystal || !this.tutorialEndCrystal) {
      console.error("Tutorial crystals not properly initialized");
      return;
    }

    // Create the connection path
    const path = [
      this.tutorialStartCrystal,
      this.crystals[3][4], // negative crystal
      this.tutorialEndCrystal,
    ];

    // Play connection sound
    if (this.soundManager) {
      this.soundManager.playConnection();
    }

    // Create energy line effects
    const energyLines = [];

    // Create first segment
    const energyLine1 = this.createEnergyLine(
      this.tutorialStartCrystal.mesh.position,
      this.crystals[3][4].mesh.position,
      this.tutorialStartCrystal.color
    );
    if (energyLine1) energyLines.push(energyLine1);

    // Create second segment
    const energyLine2 = this.createEnergyLine(
      this.crystals[3][4].mesh.position,
      this.tutorialEndCrystal.mesh.position,
      this.tutorialStartCrystal.color
    );
    if (energyLine2) energyLines.push(energyLine2);

    // Animate the connection
    const duration = 1000;
    const startTime = Date.now();

    // Wait for the animation to complete
    await new Promise((resolve) => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const time = elapsed * 0.001; // Convert to seconds

        // Update energy line effects
        energyLines.forEach((line) => {
          if (line && line.update) {
            line.update(time);
          }
        });

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Clean up energy lines
          energyLines.forEach((line) => {
            if (line && line.dispose) {
              line.dispose();
              if (line.mesh) {
                this.scene.remove(line.mesh);
              }
            }
          });
          resolve();
        }
      };
      animate();
    });

    // Play crystal removal sound
    if (this.soundManager) {
      this.soundManager.playConnected();
    }

    // Clear the connected crystals
    for (const crystal of path) {
      if (crystal && crystal.mesh) {
        this.createRemovalEffect(
          crystal.mesh.position.clone(),
          crystal.color || 0xffffff
        );
        this.scene.remove(crystal.mesh);
      }
    }

    // Wait a bit before spawning new crystals
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Play crystal spawn sound
    if (this.soundManager) {
      this.soundManager.playTap();
    }

    // Spawn new crystals
    await this.spawnNewCrystal(3, 2);
    await this.spawnNewCrystal(3, 4);
    await this.spawnNewCrystal(3, 6);
  }

  getTutorialStartCrystal() {
    return this.tutorialStartCrystal;
  }

  getTutorialEndCrystal() {
    return this.tutorialEndCrystal;
  }

  generateInitialLayout() {
    const layout = [];
    for (let row = 0; row < this.gridSize; row++) {
      for (let col = 0; col < this.gridSize; col++) {
        const type = this.getRandomCrystalType();
        const color = type === "positive" ? this.getRandomChakraColor() : null;
        layout.push({ row, col, type, color });
      }
    }
    return layout;
  }
}
